import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCart } from '../contexts/CartContext';
import Notification from './Notification';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const StyledCard = styled(Card)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 0,
  boxShadow: 'none',
  border: '1px solid #eee',
  backgroundColor: '#fff',
});

const ImageContainer = styled(Box)({
  position: 'relative',
  paddingTop: '100%', // 1:1 aspect ratio
  '&:hover .image-controls': {
    opacity: 1,
  },
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    backgroundColor: '#fff'
  }
});

const ImageControls = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 0,
  transform: 'translateY(-50%)',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 8px',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  zIndex: 1,
});

const ImageButton = styled(IconButton)({
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
});


const StyledCardContent = styled(CardContent)({
  flexGrow: 1,
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  '&:last-child': {
    paddingBottom: '1.5rem',
  },
});

const ProductTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '1.5rem',
  color: '#1a1a1a',
  textAlign: 'left',
  marginBottom: '0.75rem',
  fontWeight: 400,
  letterSpacing: '0.5px',
});

const ProductDescription = styled(Typography)({
  color: '#666',
  fontSize: '0.9rem',
  marginBottom: '1rem',
  lineHeight: 1.6,
  fontFamily: 'Cormorant Garamond, serif',
});

const ProductPrice = styled(Typography)({
  color: '#1a1a1a',
  fontSize: '1.1rem',
  marginBottom: '1.5rem',
  fontFamily: 'Cormorant Garamond, serif',
  fontWeight: 400,
});

const AddToCartButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem',
  width: '100%',
  borderRadius: 0,
  textTransform: 'none',
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '1rem',
  letterSpacing: '0.5px',
  marginTop: 'auto',
  fontWeight: 400,
  '&:hover': {
    backgroundColor: '#c4a661',
  },
  '&:disabled': {
    backgroundColor: '#666',
    color: '#fff',
  },
});

const ComingSoonBadge = styled(Box)({
  position: 'absolute',
  top: '1rem',
  right: '1rem',
  backgroundColor: '#c4a661',
  color: '#fff',
  padding: '0.5rem 1rem',
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '0.9rem',
  letterSpacing: '0.5px',
  zIndex: 1,
});

const ProductCard = ({ product, disableAddToCart, comingSoon }) => {
  const { cart, addToCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const isInCart = cart.some(item => item.id === product.id);
  
  // Handle both old 'image' property and new 'images' array
  const productImages = product.images || [product.image];
  const hasMultipleImages = productImages.length > 1;
  const currentImage = productImages[currentImageIndex];

  const handlePrevImage = (event) => {
    event.stopPropagation();
    setCurrentImageIndex(prev => 
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = (event) => {
    event.stopPropagation();
    setCurrentImageIndex(prev => 
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleAddToCart = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (loading || disableAddToCart) return;
    
    setLoading(true);
    console.log('Attempting to add to cart:', product);

    try {
      await addToCart(product);
      console.log('Successfully added to cart');
      setNotification({
        open: true,
        message: `${product.name} added to cart`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to add to cart:', error);
      setNotification({
        open: true,
        message: 'Failed to add item to cart',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [product, loading, addToCart, disableAddToCart]);

  const handleCloseNotification = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      <StyledCard>
        <ImageContainer>
          <img
            src={currentImage}
            alt={product.name}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              backgroundColor: '#fff'
            }}
            onError={(e) => {
              console.error('Image failed to load:', currentImage);
              e.target.onerror = null;
              e.target.src = '/images/products/placeholder.png';
            }}
          />
          {hasMultipleImages && (
            <ImageControls className="image-controls">
              <ImageButton onClick={handlePrevImage} size="small">
                <ChevronLeftIcon />
              </ImageButton>
              <ImageButton onClick={handleNextImage} size="small">
                <ChevronRightIcon />
              </ImageButton>
            </ImageControls>
          )}
          {comingSoon && (
            <ComingSoonBadge>Coming Soon</ComingSoonBadge>
          )}
        </ImageContainer>
        <StyledCardContent>
          <Box sx={{ mb: 'auto' }}>
            <ProductTitle>{product.name}</ProductTitle>
            <ProductDescription>
              {product.description}
            </ProductDescription>
            <ProductPrice>
              {comingSoon ? 'Coming Soon' : new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 2,
              }).format(product.price)}
            </ProductPrice>
          </Box>
          {!comingSoon && !disableAddToCart && (
            <AddToCartButton
              onClick={handleAddToCart}
              variant="contained"
              disableElevation
              disabled={loading}
            >
              {loading ? 'Adding...' : isInCart ? 'Add Another' : 'Add to Cart'}
            </AddToCartButton>
          )}
          {comingSoon && (
            <AddToCartButton
              variant="contained"
              disableElevation
              disabled
            >
              Coming Soon
            </AddToCartButton>
          )}
        </StyledCardContent>
      </StyledCard>
      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
        autoHideDuration={3000}
      />
    </>
  );
};

export default ProductCard;
